<script setup>
import FormLabel from "./Label.vue";
import FormErrors from "./Errors.vue";
import _uniqueId from "lodash/uniqueId";
import { computed, ref, useSlots } from "vue";
import { twMerge } from "tailwind-merge";
import { FormHelp, FormHint } from ".";
import Tooltip from "@Components/Tooltip.vue";
import { LoadingIcon } from "@Icons/index";
import { Maximize2Icon, WandSparklesIcon, XIcon } from "lucide-vue-next";
import { Modal } from "@Components/Modal";
import { useI18n } from "vue-i18n";
import { useForm } from "@/form";

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    modelValue: {
        type: String,
        default: "",
    },
    label: {
        type: String,
        default: undefined,
    },
    hint: {
        type: String,
    },
    help: {
        type: String,
    },
    z: {
        type: Number,
        default: 0,
    },
    required: {
        type: Boolean,
        default: false,
    },
    errors: {
        type: Array,
        default: [],
    },
    leftActions: {
        type: Array,
        default: [],
    },
    rightActions: {
        type: Array,
        default: [],
    },
    class: String,
});
const emit = defineEmits(["update:modelValue"]);

const slots = useSlots();
const { t: __ } = useI18n();

const key = _uniqueId("textarea-");
const modalKey = _uniqueId("modal-textarea-");
const textarea = ref(null);
const modal = ref(null);
const form = useForm({
    prompt: "",
});

const wrapperClasses = computed(() => {
    let classes = [
        "rounded-md border-0 ring-1 ring-inset shadow-sm focus-within:ring-2 focus-within:ring-inset",
        "bg-white ring-gray-300 focus-within:ring-gray-950",
    ];

    switch (props.z) {
        case 3:
            classes.push("dark:bg-gray-600 dark:ring-gray-500 dark:focus-within:ring-gray-400");
            break;
        case 2:
            classes.push("dark:bg-gray-700 dark:ring-gray-600 dark:focus-within:ring-gray-500");
            break;
        case 1:
            classes.push("dark:bg-gray-800 dark:ring-gray-700 dark:focus-within:ring-gray-600");
            break;
        default:
            classes.push("dark:bg-gray-900 dark:ring-gray-800 dark:focus-within:ring-gray-700");
            break;
    }

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus:ring-red-500 dark:ring-red-500 dark:focus-within:ring-red-500");
    }

    return twMerge(classes);
});

const modalWrapperClasses = computed(() => {
    let classes = [
        "rounded-md border-0 ring-1 ring-inset shadow-sm focus-within:ring-2 focus-within:ring-inset",
        "bg-white ring-gray-300 focus-within:ring-gray-950",
    ];

    classes.push("dark:bg-gray-800 dark:ring-gray-700 dark:focus-within:ring-gray-600");

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus:ring-red-500 dark:ring-red-500 dark:focus-within:ring-red-500");
    }

    return twMerge(classes);
});

const textareaClasses = computed(() => {
    let classes = [
        "w-full block px-3 py-2 text-sm leading-6 bg-transparent border-none resize-none scroll-px-3 scroll-py-2 focus:outline-0 focus:outline",
        "text-gray-900 placeholder:text-gray-400 dark:text-white",
    ];

    switch (props.z) {
        case 3:
            classes.push("dark:placeholder:text-gray-300");
            break;
        case 2:
            classes.push("dark:placeholder:text-gray-400");
            break;
        case 1:
            classes.push("dark:placeholder:text-gray-400");
            break;
        default:
            classes.push("dark:placeholder:text-gray-400");
            break;
    }

    return twMerge(classes);
});

const modalTextareaClasses = computed(() => {
    let classes = [
        "w-full block px-3 py-2 text-sm leading-6 bg-transparent border-none resize-none scroll-px-3 scroll-py-2 focus:outline-0 focus:outline",
        "text-gray-900 placeholder:text-gray-400 dark:text-white",
    ];

    classes.push("dark:placeholder:text-gray-400");

    return twMerge(classes);
});

const rewritePrompt = async () => {
    if (props.modelValue.trim().length === 0) return;
    if (form.processing) return;

    form.prompt = props.modelValue;

    const response = await form.post("one_click_rewrite_prompt");

    if (response.rewritten_prompt && response.rewritten_prompt.trim().length > 0) {
        emit("update:modelValue", response.rewritten_prompt);
    }
};
</script>

<template>
    <div class="flex flex-col">
        <div :class="[label ? 'justify-between' : 'justify-end', 'flex items-center space-x-3']">
            <FormLabel v-if="label" :for="key">
                {{ label }}
            </FormLabel>
            <slot name="hint">
                <FormHint v-if="hint" :z="z">
                    {{ hint }}
                </FormHint>
            </slot>
        </div>

        <div :class="[label || hint || !!slots.hint ? 'mt-2' : '', wrapperClasses]" @click="$refs['textarea'].focus()">
            <textarea
                ref="textarea"
                :value="modelValue"
                @input="$emit('update:modelValue', $event.target.value)"
                :id="key"
                :required="required"
                v-bind="$attrs"
                :class="textareaClasses" />

            <div v-if="leftActions.length > 0 || rightActions.length > 0" class="flex justify-between items-center gap-3 px-3 pt-0.5 pb-2">
                <div v-if="leftActions.length > 0" class="flex gap-1.5">
                    <template v-for="(action, index) in leftActions" :key="index">
                        <div v-if="action.id === 'magic-prompt'">
                            <button
                                @click.stop="rewritePrompt"
                                type="button"
                                :disabled="form.processing || !modelValue || modelValue?.trim().length === 0"
                                :class="[
                                    'flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                    'bg-gray-50 rounded-md dark:bg-gray-800 dark:hover:bg-gray-700 hover:bg-gray-100 px-2 py-1',
                                    form.processing || !modelValue || modelValue?.trim().length === 0 ? 'pointer-events-none' : '',
                                ]">
                                <LoadingIcon v-if="form.processing" class="size-4" />
                                <WandSparklesIcon v-else class="size-4" />
                                <div class="text-xs">
                                    {{ __("Améliorer le prompt") }}
                                </div>
                            </button>
                        </div>
                        <component v-else :is="action.tooltip ? Tooltip : 'div'" :content="action.tooltip" placement="top">
                            <button
                                @click.stop="action.onClick ? action.onClick() : null"
                                type="button"
                                :disabled="action.processing || action.disabled"
                                :class="[
                                    'flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                    action.label ? 'bg-gray-50 rounded-md dark:bg-gray-800 dark:hover:bg-gray-700 hover:bg-gray-100 px-2 py-1' : '',
                                    action.processing || action.disabled ? 'pointer-events-none' : '',
                                ]">
                                <LoadingIcon v-if="action.processing" class="size-4" />
                                <component v-else-if="action.icon" :is="action.icon" class="size-4" />
                                <div v-if="action.label" class="text-xs">
                                    {{ action.label }}
                                </div>
                            </button>
                        </component>
                    </template>
                </div>
                <div v-else></div>
                <div v-if="rightActions.length > 0" class="flex gap-1.5">
                    <template v-for="(action, index) in rightActions" :key="index">
                        <Tooltip v-if="action.id === 'maximize'" :content="__('Agrandir')" placement="top">
                            <button
                                @click.stop="$refs['modal'].show()"
                                type="button"
                                :class="[
                                    'flex items-center gap-1.5  cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                ]">
                                <Maximize2Icon class="size-4" />
                            </button>
                        </Tooltip>
                        <component v-else :is="action.tooltip ? Tooltip : 'div'" :content="action.tooltip" placement="top">
                            <button
                                @click.stop="action.onClick ? action.onClick() : null"
                                type="button"
                                :disabled="action.processing || action.disabled"
                                :class="[
                                    'flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                    action.label ? 'bg-gray-50 rounded-md dark:bg-gray-800 dark:hover:bg-gray-700 hover:bg-gray-100 px-2 py-1' : '',
                                    action.processing || action.disabled ? 'pointer-events-none' : '',
                                ]">
                                <LoadingIcon v-if="action.processing" class="size-4" />
                                <component v-else-if="action.icon" :is="action.icon" class="size-4" />
                                <div v-if="action.label" class="text-xs">
                                    {{ action.label }}
                                </div>
                            </button>
                        </component>
                    </template>
                </div>
                <div v-else></div>
            </div>
        </div>

        <slot name="help">
            <FormHelp v-if="help" class="mt-2">
                {{ help }}
            </FormHelp>
        </slot>

        <FormErrors v-if="errors" class="mt-2" :errors="errors" />

        <Modal ref="modal" class="sm:w-5/6 sm:max-w-none sm:h-5/6 sm:max-h-none">
            <div class="absolute top-2 right-2">
                <XIcon
                    class="size-5 text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900"
                    @click="$refs['modal'].hide()" />
            </div>
            <div class="px-6 py-12">
                <div :class="[label ? 'justify-between' : 'justify-end', 'flex items-center space-x-3']">
                    <FormLabel v-if="label" :for="modalKey">
                        {{ label }}
                    </FormLabel>
                    <slot name="hint">
                        <FormHint v-if="hint" :z="z">
                            {{ hint }}
                        </FormHint>
                    </slot>
                </div>
                <div :class="[label || hint || !!slots.hint ? 'mt-2' : '', modalWrapperClasses]">
                    <textarea
                        :id="modalKey"
                        :value="modelValue"
                        @input="$emit('update:modelValue', $event.target.value)"
                        v-bind="$attrs"
                        :class="modalTextareaClasses"
                        :rows="25"></textarea>

                    <div v-if="leftActions.length > 0 || rightActions.length > 0" class="flex justify-between items-center gap-3 px-3 pt-0.5 pb-2">
                        <div v-if="leftActions.length > 0" class="flex gap-1.5">
                            <template v-for="(action, index) in leftActions" :key="index">
                                <div v-if="action.id === 'magic-prompt'">
                                    <button
                                        @click.stop="rewritePrompt"
                                        type="button"
                                        :disabled="form.processing || !modelValue || modelValue?.trim().length === 0"
                                        :class="[
                                            'flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                            'bg-gray-50 rounded-md dark:bg-gray-700 dark:hover:bg-gray-600 hover:bg-gray-100 px-2 py-1',
                                            form.processing || !modelValue || modelValue?.trim().length === 0 ? 'pointer-events-none' : '',
                                        ]">
                                        <LoadingIcon v-if="form.processing" class="size-4" />
                                        <WandSparklesIcon v-else class="size-4" />
                                        <div class="text-xs">
                                            {{ __("Améliorer le prompt") }}
                                        </div>
                                    </button>
                                </div>
                                <component v-else :is="action.tooltip ? Tooltip : 'div'" :content="action.tooltip" placement="top">
                                    <button
                                        @click.stop="action.onClick ? action.onClick() : null"
                                        type="button"
                                        :disabled="action.processing || action.disabled"
                                        :class="[
                                            'flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                            action.label ? 'bg-gray-50 rounded-md dark:bg-gray-700 dark:hover:bg-gray-600 hover:bg-gray-100 px-2 py-1' : '',
                                            action.processing || action.disabled ? 'pointer-events-none' : '',
                                        ]">
                                        <LoadingIcon v-if="action.processing" class="size-4" />
                                        <component v-else-if="action.icon" :is="action.icon" class="size-4" />
                                        <div v-if="action.label" class="text-xs">
                                            {{ action.label }}
                                        </div>
                                    </button>
                                </component>
                            </template>
                        </div>
                        <div v-else></div>
                        <div v-if="rightActions.length > 0" class="flex gap-1.5">
                            <template v-for="(action, index) in rightActions" :key="index">
                                <component :is="action.tooltip ? Tooltip : 'div'" :content="action.tooltip" placement="top">
                                    <button
                                        @click.stop="action.onClick ? action.onClick() : null"
                                        type="button"
                                        :disabled="action.processing || action.disabled"
                                        :class="[
                                            'flex items-center gap-1.5 cursor-pointer text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 hover:text-gray-900',
                                            action.label ? 'bg-gray-50 rounded-md dark:bg-gray-700 dark:hover:bg-gray-600 hover:bg-gray-100 px-2 py-1' : '',
                                            action.processing || action.disabled ? 'pointer-events-none' : '',
                                        ]">
                                        <LoadingIcon v-if="action.processing" class="size-4" />
                                        <component v-else-if="action.icon" :is="action.icon" class="size-4" />
                                        <div v-if="action.label" class="text-xs">
                                            {{ action.label }}
                                        </div>
                                    </button>
                                </component>
                            </template>
                        </div>
                        <div v-else></div>
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>
